/**
 * dayLegs — the pure model behind the rep's clock-in / clock-out leg chain.
 *
 * A rep's day is an APPEND-ONLY list of legs:
 *
 *   clock ─┬─ drive ─ store ─ drive ─ store ─ drive ─┬─ (clock closed)
 *          └── one clock leg = one clock-in/out window
 *
 * A calendar day may hold ANY number of clock legs (split shifts), and
 * everything the UI reads — status, currentActivity, currentStoreId, totals —
 * is DERIVED from the list rather than stored alongside it.
 *
 * This module is pure and synchronous: the hook owns React state, IndexedDB
 * and GPS; this file owns the model, which is what makes the derivation
 * directly unit-testable. (#3452)
 */

export type DayStatus = 'not_started' | 'active' | 'ended';
export type ActivityType = 'idle' | 'driving' | 'in_store';

/**
 * `clock` bounds a work window; `drive`/`store` are the activity legs inside it.
 * `other` is reserved for non-store time attributed to a generic project
 * (admin / training / meetings — see plan §2a-SIMPLIFIED); nothing emits it yet.
 */
export type LegType = 'clock' | 'drive' | 'store' | 'other';

export interface Location {
  lat: number;
  lng: number;
  timestamp: Date;
  accuracy?: number;
}

export interface TimeEntry {
  id: string;
  type: LegType;
  storeName?: string;
  storeId?: number;
  projectId?: number;
  projectName?: string;
  startTime: Date;
  endTime?: Date;
  startLocation?: Location;
  endLocation?: Location;
  durationMinutes?: number;
  startOdometer?: number;
  endOdometer?: number;
  callLogId?: number;
  // Set when the task at this store was abandoned — the server CallLog is
  // voided and must NOT receive drive-back odo at end-of-day.  #1507.
  voided?: boolean;
  // True when this leg was created/edited by the rep rather than punched live
  // (retroactive start, Time-page fix).
  manual?: boolean;
  // client_event_id of the punch that opened / closed this leg. A later
  // correction names these so the server can supersede the original event
  // instead of the ledger seeing two conflicting punches.
  startEventId?: string;
  endEventId?: string;
}

export interface ClosedDriveData {
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
  startOdometer?: number;
  endOdometer?: number;
}

/** The day view consumed by Today, StoreDetail, CallDetail, SetDetail and TimeTracking. */
export interface DayData {
  status: DayStatus;
  startTime: Date | null;
  endTime: Date | null;
  startLocation: Location | null;
  currentLocation: Location | null;
  currentActivity: ActivityType;
  currentStoreId: number | null;
  currentProjectId: number | null;
  timeEntries: TimeEntry[];
  totalDriveMinutes: number;
  totalStoreMinutes: number;
  totalMiles: number;
}

/**
 * A live leg open longer than this is a punch the rep forgot to close, not
 * real elapsed work (triage #2 — a driving timer reading 20+ hours). Reads
 * clamp to it so an unclosed leg can never inflate a running total; the leg
 * itself stays open and correctable.
 */
export const MAX_OPEN_LEG_MINUTES = 12 * 60;

/**
 * Where a drive stops being long and starts being suspicious (#3518).
 *
 * MAX_OPEN_LEG_MINUTES bounds what an open leg DISPLAYS, but the duration
 * written when the leg closes was never bounded at all, so a clock-in left
 * running overnight recorded an 8h25m drive and added every minute of it to the
 * day's drive total.
 */
export const MAX_PLAUSIBLE_DRIVE_MINUTES = 4 * 60;

/**
 * Slowest average a real drive runs at. Duration alone cannot separate a
 * forgotten punch from a long haul, and prod says so: of 33,978 recorded drive
 * segments this year, 39 ran past four hours, and the odometer splits them
 * cleanly. The real ones cover 237-350 miles at 47-60 mph. The forgotten
 * punches cover 5 miles in 6h15m, 13 in 23h45m, or no distance at all — which
 * is exactly what diagnostic #82 looked like: 8h25m, odometer 0 to 0.
 *
 * So the test is not "how long" but "did the rep actually go anywhere". A flat
 * four-hour cutoff would have thrown away seven genuine long-haul drives.
 */
export const MIN_DRIVING_MPH = 20;

/**
 * A forgotten punch wearing a drive's clothes: long enough to be doubted, and
 * with no distance to back it up.
 *
 * Such a leg is kept and shown — deleting a rep's data on a guess is worse —
 * but left out of the totals so it cannot inflate the day, never offered as the
 * starting point for a manual entry, and never sent as a drive segment on a
 * real CallLog.
 *
 * PLACEHOLDER pending Beth/James, same bucket as the commute rule and the
 * rounding policy.
 */
export function isImplausibleDriveSpan(minutes: number, miles: number | null): boolean {
  if (minutes <= MAX_PLAUSIBLE_DRIVE_MINUTES) return false;
  // No usable odometer pair is not evidence of standing still — it is no
  // evidence at all. Prod says so plainly: not one of 36 recorded drive_ended
  // events carries an end reading, because the reading is only seeded by Start
  // Day and most reps reach their first store before clocking in (#3519). Held
  // to the four-hour rule those drives would all be discarded, which turned a
  // guard against forgotten punches into a machine for deleting real drives.
  // Judged on time alone, a drive gets the benefit of the doubt until it is
  // long enough that no reading could redeem it.
  if (miles == null) return minutes > MAX_OPEN_LEG_MINUTES;
  return miles / (minutes / 60) < MIN_DRIVING_MPH;
}

export function isImplausibleDrive(leg: TimeEntry): boolean {
  if (leg.type !== 'drive') return false;
  const minutes = leg.endTime
    ? spanMinutes(leg.startTime, leg.endTime)
    : spanMinutes(leg.startTime, new Date());
  return isImplausibleDriveSpan(minutes, legMiles(leg));
}

export const initialDayData: DayData = {
  status: 'not_started',
  startTime: null,
  endTime: null,
  startLocation: null,
  currentLocation: null,
  currentActivity: 'idle',
  currentStoreId: null,
  currentProjectId: null,
  timeEntries: [],
  totalDriveMinutes: 0,
  totalStoreMinutes: 0,
  totalMiles: 0,
};

// ---------- primitives ----------

/**
 * Minutes between two instants. Both are absolute timestamps, so a span across
 * midnight is nothing special — the client-side twin of the server's single
 * `duration.span_minutes` (#3450). A negative span (only reachable via a manual
 * edit) clamps to 0 rather than leaking a bogus total.
 */
export function spanMinutes(start: Date, end: Date): number {
  const ms = end.getTime() - start.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.round(ms / 60000);
}

export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Miles for a drive leg: the odometer delta, or null.
 *
 * The odometer is the ONLY source of mileage. Every state transition prompts
 * for a reading pre-filled with the last one, so a leg either carries both
 * readings and has real miles, or the rep skipped the prompt and the miles are
 * honestly unknown. There is deliberately no GPS-derived fallback — a second,
 * softer number would only invite an estimate into fields that bill.
 *
 * An EQUAL non-zero pair is an honest zero: whole-mile odometers don't move on
 * a short hop, and the rep affirmed both readings (#3618). A 0/0 pair means
 * mileage wasn't tracked at all, so it stays null like a missing reading.
 */
export function odometerMiles(
  from: number | null | undefined,
  to: number | null | undefined,
): number | null {
  if (from == null || to == null || to < from) return null;
  if (from === 0 && to === 0) return null;
  return Math.round((to - from) * 10) / 10;
}

export function legMiles(leg: TimeEntry | undefined): number | null {
  if (!leg || leg.type !== 'drive') return null;
  return odometerMiles(leg.startOdometer, leg.endOdometer);
}

export function generateLegId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Rep-local calendar date, `YYYY-MM-DD`. The device clock IS the rep's tz. */
export function dayKeyOf(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** IDB row key for a day. `visit_day_sessions` keys on a numeric `id`. */
export function rowIdForDayKey(dayKey: string): number {
  return Number(dayKey.replace(/-/g, ''));
}

export function daysBetweenKeys(a: string, b: string): number {
  const toUtc = (k: string) => {
    const [y, m, d] = k.split('-').map(Number);
    return Date.UTC(y, (m || 1) - 1, d || 1);
  };
  return Math.round((toUtc(a) - toUtc(b)) / 86400000);
}

// ---------- leg queries ----------

export function openLegOfType(legs: TimeEntry[], type: LegType): TimeEntry | undefined {
  return legs.find((l) => l.type === type && !l.endTime);
}

export function hasOpenClock(legs: TimeEntry[]): boolean {
  return legs.some((l) => l.type === 'clock' && !l.endTime);
}

/**
 * When a clock window has to be opened after the fact, the instant it has to
 * start from (#3519).
 *
 * A clock window must ENCLOSE the work inside it, or the day stops being
 * contiguous: a drive opened at the last checkout, still running when the rep
 * checks in somewhere without an open clock, began before any punch we are
 * about to write. Clocking in at the check-in moment would strand it outside
 * the window, where the totals cannot see it. So the window starts at the
 * earliest thing it needs to cover, and only falls back to `at` when there is
 * nothing open to cover.
 */
export function enclosingStartFor(legs: TimeEntry[], at: Date): Date {
  const open = legs.filter((l) => l.type !== 'clock' && !l.endTime);
  if (!open.length) return at;
  const earliest = open.reduce((a, b) => (a.startTime <= b.startTime ? a : b));
  return earliest.startTime < at ? earliest.startTime : at;
}

/** The activity legs (everything that isn't a clock boundary), oldest first. */
export function activityLegs(legs: TimeEntry[]): TimeEntry[] {
  return legs
    .filter((l) => l.type !== 'clock')
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

/** The clock-in/out windows of the day, oldest first. */
export function clockLegs(legs: TimeEntry[]): TimeEntry[] {
  return legs
    .filter((l) => l.type === 'clock')
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

/**
 * What a well-formed day looks like (#3519).
 *
 * The rule: no overlaps, and contiguous time. Inside a clock window the
 * activity legs should tile the timeline — the rep is driving, or in a store,
 * or the day has a hole in it nobody can account for.
 *
 * The overlap test is the SERVER's, not a third opinion: `start1 < end2 &&
 * end1 > start2` on closed pairs (apps/projects/apis/serializer.py,
 * datetime_ranges_overlap), the same half-open rule timesheetOverlap already
 * mirrors for the timesheet. A CallLog breaking it is rejected outright, so a
 * chain breaking it is a queued write with nowhere to land.
 *
 * These REPORT rather than refuse. A rep is never blocked from recording work
 * because the app dislikes the shape of it — the caller decides what to do, and
 * the tests use this as the invariant.
 */
export interface DayProblem {
  kind: 'overlap' | 'gap';
  legIds: string[];
  from: Date;
  to: Date;
  minutes: number;
}

function rangesOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date): boolean {
  return aStart < bEnd && aEnd > bStart;
}

/** Closed activity legs occupying the same moment. An open leg claims no span yet. */
export function findOverlaps(legs: TimeEntry[]): DayProblem[] {
  const closed = activityLegs(legs).filter((l) => l.endTime);
  const problems: DayProblem[] = [];
  for (let i = 0; i < closed.length; i++) {
    for (let j = i + 1; j < closed.length; j++) {
      const a = closed[i];
      const b = closed[j];
      if (!rangesOverlap(a.startTime, a.endTime!, b.startTime, b.endTime!)) continue;
      const from = a.startTime > b.startTime ? a.startTime : b.startTime;
      const to = a.endTime! < b.endTime! ? a.endTime! : b.endTime!;
      problems.push({ kind: 'overlap', legIds: [a.id, b.id], from, to, minutes: spanMinutes(from, to) });
    }
  }
  return problems;
}

/**
 * Unaccounted stretches inside a clock window. Under a minute is the boundary
 * between two legs recorded to the minute, not a hole in the day.
 */
export function findGaps(legs: TimeEntry[], now: Date = new Date()): DayProblem[] {
  const problems: DayProblem[] = [];
  for (const window of clockLegs(legs)) {
    const end = window.endTime ?? now;
    // A leg counts wherever it OVERLAPS the window, not only when it starts
    // inside it — a drive straddling a corrected clock-in still covers the
    // window's head, and dropping it declared covered time a hole.
    const inside = activityLegs(legs)
      .filter((l) => (l.endTime ?? end) > window.startTime && l.startTime < end)
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

    let cursor = window.startTime;
    for (const leg of inside) {
      const legStart = leg.startTime > window.startTime ? leg.startTime : window.startTime;
      if (spanMinutes(cursor, legStart) >= 1) {
        problems.push({
          kind: 'gap',
          legIds: [leg.id],
          from: cursor,
          to: legStart,
          minutes: spanMinutes(cursor, legStart),
        });
      }
      const legEnd = leg.endTime ?? end;
      if (legEnd > cursor) cursor = legEnd;
    }
    // A window closed with time left over after its last leg is a gap too.
    if (window.endTime && spanMinutes(cursor, window.endTime) >= 1) {
      problems.push({
        kind: 'gap',
        legIds: [],
        from: cursor,
        to: window.endTime,
        minutes: spanMinutes(cursor, window.endTime),
      });
    }
  }
  return problems;
}

/**
 * Total minutes of gap the day carries — pay-window time no drive or visit
 * accounts for. This is the number the summary surfaces as "Unassigned", so a
 * bare stretch can never sit silently between Total Hours and Store+Drive.
 * Overlaps are a different disease (two claims on the same minutes, not a
 * hole) and contribute nothing. Summed over the UNION of the gap spans:
 * overlapping clock windows each report the hole between them, and a rep must
 * not be sent chasing twice the time that is actually missing.
 */
export function unassignedMinutes(problems: DayProblem[]): number {
  const gaps = problems
    .filter((p) => p.kind === 'gap')
    .map((p) => [p.from.getTime(), p.to.getTime()] as const)
    .sort((a, b) => a[0] - b[0]);
  let total = 0;
  let curStart: number | null = null;
  let curEnd = 0;
  for (const [start, end] of gaps) {
    if (curStart === null || start > curEnd) {
      if (curStart !== null) total += curEnd - curStart;
      curStart = start;
      curEnd = end;
    } else if (end > curEnd) {
      curEnd = end;
    }
  }
  if (curStart !== null) total += curEnd - curStart;
  return Math.round(total / 60000);
}

/**
 * The one number both Today and the Time page call "unaccounted": clocked-in
 * minutes no visit or drive covers, from the same chain both render. A page
 * deriving its own copy invites the two surfaces to disagree about the hole
 * one of them links to.
 */
export function dayUnassignedMinutes(clockWindows: TimeEntry[], timeEntries: TimeEntry[]): number {
  const legs = [...clockWindows, ...timeEntries];
  if (!legs.length) return 0;
  return unassignedMinutes(findGaps(legs));
}

/**
 * The store visit a gap most plausibly drove to: the leg the gap runs into,
 * when that leg is a store visit carrying a synced CallLog. A drive has no
 * server home of its own — it lives on a visit's drive fields — so a gap can
 * only be "driving" when there is a visit to attach it to. Tail gaps (after
 * the day's last leg) and gaps running into unsynced or non-store legs offer
 * nothing.
 */
export function gapDriveCandidate(gap: DayProblem, legs: TimeEntry[]): TimeEntry | null {
  const nextId = gap.legIds[0];
  if (!nextId) return null;
  const next = legs.find((l) => l.id === nextId);
  if (!next || next.type !== 'store' || !next.callLogId) return null;
  return next;
}

/**
 * The closed leg of `type` that immediately precedes `before` in the chain —
 * the drive the rep just finished when they check in, or the store they just
 * left when the next drive opens.
 */
export function previousClosedLeg(
  legs: TimeEntry[],
  before: TimeEntry | null | undefined,
  type: LegType
): TimeEntry | undefined {
  if (!before) return undefined;
  const entries = activityLegs(legs);
  const index = entries.findIndex((l) => l.id === before.id);
  if (index <= 0) return undefined;
  for (let i = index - 1; i >= 0; i--) {
    if (entries[i].type === type && entries[i].endTime) return entries[i];
  }
  return undefined;
}

/** Elapsed minutes on an open leg, clamped so a forgotten punch can't run away. */
export function elapsedMinutesFor(leg: TimeEntry, now: number = Date.now()): number {
  const raw = spanMinutes(leg.startTime, new Date(now));
  return Math.min(raw, MAX_OPEN_LEG_MINUTES);
}

// ---------- leg transforms (all return a NEW list) ----------

/**
 * Close every open leg matching `match` at `at`, stamping duration and (when
 * supplied) the end location and odometer.
 *
 * The odometer is only stamped on a drive when the reading has actually moved
 * past the one the leg opened with. The reading arriving here can be an
 * auto-carried one from a skipped prompt, so an unchanged value is not a
 * rep-affirmed zero — it is no reading at all, and stamping it would fabricate
 * a 0-mile drive (#2081). Affirmed equal pairs enter through the manual-entry
 * surfaces, where the rep types both readings (#3618).
 */
export function closeLegs(
  legs: TimeEntry[],
  match: (leg: TimeEntry) => boolean,
  at: Date,
  location?: Location | null,
  odometer?: number | null
): TimeEntry[] {
  return legs.map((leg) => {
    if (leg.endTime || !match(leg)) return leg;
    const closed: TimeEntry = {
      ...leg,
      endTime: at,
      durationMinutes: spanMinutes(leg.startTime, at),
      endLocation: location ?? leg.endLocation,
    };
    if (
      odometer != null &&
      closed.endOdometer == null &&
      (leg.type !== 'drive' || leg.startOdometer == null || odometer > leg.startOdometer)
    ) {
      closed.endOdometer = odometer;
    }
    return closed;
  });
}

export function closedDriveDataFrom(leg: TimeEntry | undefined, at: Date): ClosedDriveData | undefined {
  if (!leg) return undefined;
  return {
    startTime: leg.startTime,
    endTime: at,
    durationMinutes: spanMinutes(leg.startTime, at),
    startOdometer: leg.startOdometer,
    endOdometer: leg.endOdometer,
  };
}

// ---------- derivation ----------

export function deriveDayData(legs: TimeEntry[], currentLocation: Location | null): DayData {
  const clockLegs = legs
    .filter((l) => l.type === 'clock')
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const entries = activityLegs(legs);

  const openClock = clockLegs.find((l) => !l.endTime);
  const status: DayStatus = clockLegs.length === 0 ? 'not_started' : openClock ? 'active' : 'ended';

  const openStore = openLegOfType(entries, 'store');
  const openDrive = openLegOfType(entries, 'drive');
  const currentActivity: ActivityType =
    status !== 'active' ? 'idle' : openStore ? 'in_store' : openDrive ? 'driving' : 'idle';

  const lastClock = clockLegs[clockLegs.length - 1];
  const totalDriveMinutes = sumClosed(entries, 'drive');
  const totalStoreMinutes = sumClosed(entries, 'store');

  let totalMiles = 0;
  entries.forEach((e) => {
    if (e.type !== 'drive' || !e.endTime) return;
    totalMiles += legMiles(e) ?? 0;
  });

  return {
    status,
    startTime: clockLegs[0]?.startTime ?? null,
    endTime: status === 'ended' ? lastClock?.endTime ?? null : null,
    startLocation: clockLegs[0]?.startLocation ?? null,
    currentLocation,
    currentActivity,
    currentStoreId: openStore?.storeId ?? null,
    currentProjectId: openStore?.projectId ?? null,
    // Clock legs are deliberately NOT surfaced here: consumers scan
    // `timeEntries` for "the open entry" and for drive/store pairs, and an
    // always-open clock leg would shadow both. Phase E exposes the full chain
    // separately for editing.
    timeEntries: entries,
    totalDriveMinutes,
    totalStoreMinutes,
    totalMiles: Math.round(totalMiles * 10) / 10,
  };
}

function sumClosed(entries: TimeEntry[], type: LegType): number {
  return entries
    .filter((e) => e.type === type)
    // A drive too long to be one is a forgotten punch. It stays visible for the
    // rep to correct, but adding it here put 8h25m of "drive" on a day that had
    // none (#3518).
    .filter((e) => !isImplausibleDrive(e))
    .reduce((sum, e) => sum + (e.durationMinutes || 0), 0);
}

// ---------- revive / migrate ----------

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  return new Date(v as string);
}

function toLocation(loc: unknown): Location | null {
  if (!loc) return null;
  const l = loc as Partial<Location>;
  if (l.lat == null || l.lng == null) return null;
  return { ...l, timestamp: toDate(l.timestamp) ?? new Date(0) } as Location;
}

export function reviveLeg(raw: unknown): TimeEntry {
  const e = raw as Partial<TimeEntry>;
  return {
    ...(e as TimeEntry),
    startTime: toDate(e.startTime) as Date,
    endTime: e.endTime ? (toDate(e.endTime) as Date) : undefined,
    startLocation: e.startLocation ? toLocation(e.startLocation) ?? undefined : undefined,
    endLocation: e.endLocation ? toLocation(e.endLocation) ?? undefined : undefined,
  };
}

export function reviveLegs(raw: unknown): TimeEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(reviveLeg)
    .filter((l) => l.startTime instanceof Date && !Number.isNaN(l.startTime.getTime()));
}

/**
 * Revive a stored/served `DayData` payload (legacy IDB singleton, legacy
 * localStorage blob, or the server's `/active-day` response).
 */
export function reviveDayData(raw: Record<string, unknown>): DayData {
  const data = raw as Partial<DayData>;
  return {
    status: data.status ?? 'not_started',
    startTime: toDate(data.startTime),
    endTime: toDate(data.endTime),
    startLocation: toLocation(data.startLocation),
    currentLocation: toLocation(data.currentLocation),
    currentActivity: data.currentActivity ?? 'idle',
    currentStoreId: data.currentStoreId ?? null,
    currentProjectId: data.currentProjectId ?? null,
    timeEntries: reviveLegs(data.timeEntries),
    totalDriveMinutes: data.totalDriveMinutes ?? 0,
    totalStoreMinutes: data.totalStoreMinutes ?? 0,
    totalMiles: data.totalMiles ?? 0,
  };
}

/**
 * One-time conversion of the pre-#3452 single-session `DayData` into a leg
 * chain: its start/end become ONE clock leg wrapping the existing entries.
 * Used for the legacy IDB singleton, the legacy localStorage blob, and the
 * server's active-day rehydrate (#3354), which all speak `DayData`.
 */
export function legsFromDayData(day: DayData): TimeEntry[] {
  if (day.status === 'not_started' || !day.startTime) return [];
  const clock: TimeEntry = {
    id: `${day.startTime.getTime()}-clock`,
    type: 'clock',
    startTime: day.startTime,
    startLocation: day.startLocation ?? undefined,
  };
  if (day.status === 'ended' && day.endTime) {
    clock.endTime = day.endTime;
    clock.durationMinutes = spanMinutes(day.startTime, day.endTime);
  }
  // Entries carried a `drive | store` type that is a strict subset of LegType,
  // so they transfer as-is.
  return [clock, ...day.timeEntries];
}
